import type { IFieldDefinition, IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import type { IChildRelation } from './IChildRelation';
import { LAW_TABS } from './lawdesigner/lawTabs';

/** Вкладка форми метаданих, що розгортається у вкладки візуальної моделі закону (прототип v4: lds — Структура … Правки). */
const LAW_DESIGNER = 'lawdesigner';

/**
 * Вкладки запису в порядку прототипу v4: [Структура … Правки — конструктор законопроєкту] [Візуалізація] [Читання · Редагування]
 * Загальне · Доручення · Дочірні записи · Пов'язані елементи · Коментарі · Файли · Історія.
 */
export const standardTabs: readonly string[] = [
  ...LAW_TABS, 'visualization', 'read', 'edit', 'general', 'assignments', 'children', 'related', 'comments', 'files', 'history',
];
/** Вкладки, які показуються лише з власним рендерером (читання статті, візуалізація). */
const RENDERED: readonly string[] = ['visualization', 'read', 'edit'];

/**
 * Дочірні таблиці типу запису: типи модуля з `parent` = цей тип або з `embedded`, що мають поле-посилання (ref/lookup) на цей тип.
 * Без такого поля вбудований список відфільтрувати неможливо — тип пропускається.
 */
export function childRelationsOf(definition: IModuleDefinition, rt: IRecordTypeDefinition): IChildRelation[] {
  const relations: IChildRelation[] = [];
  for (const child of definition.recordTypes) {
    if (child.key === rt.key || !(child.parent === rt.key || child.embedded)) continue;
    const lookup = child.fields.find((f) => f.ref === rt.key && (f.type === 'ref' || f.type === 'lookup'));
    if (lookup) relations.push({ child, lookup });
  }
  return relations;
}

/** Поле ієрархії запису — посилання типу на самого себе (у Завдань — «Батьківське завдання»); без нього вкладки «Дочірні» немає. */
export function selfParentFieldOf(rt: IRecordTypeDefinition): IFieldDefinition | undefined {
  const own = rt.fields.filter((f) => (f.type === 'ref' || f.type === 'lookup') && f.ref === rt.key);
  return own.find((f) => f.key === 'parent') ?? own[0];
}

/**
 * Ключі вкладок запису: вкладки `form.tabs` визначення у стандартному порядку; `lawdesigner` — шість вкладок моделі закону першими;
 * «Дочірні записи» — коли тип має ієрархію (поле на самого себе), як «Дочірні завдання» прототипу; читання й редагування статті — з
 * `form.tabs`, якщо передано в `rendered`; «Візуалізація» — щойно запис є діаграмою (`rendered`), як vizOk прототипу. Чиста функція.
 */
export function tabKeysOf(formTabs: readonly string[], hasChildRecords: boolean, rendered: readonly string[] = []): string[] {
  return standardTabs.filter((k) => {
    if (k === 'general') return true;
    if (LAW_TABS.includes(k)) return formTabs.includes(LAW_DESIGNER);
    if (k === 'children') return hasChildRecords;
    if (k === 'visualization') return rendered.includes(k);
    if (RENDERED.includes(k)) return formTabs.includes(k) && rendered.includes(k);
    return formTabs.includes(k);
  });
}

/**
 * Вкладка запису за замовчуванням (без вкладки в адресі; прототип v4): законопроєкт конструктора — «Структура», запис-діаграма —
 * «Візуалізація», решта — «Загальне» (і стаття: «Читання» відкривають лише бібліотека та посилання статей — явною вкладкою `read`).
 */
export function defaultTabOf(rt: IRecordTypeDefinition, hasVisualization = false): string {
  if (rt.form.tabs.includes(LAW_DESIGNER)) return LAW_TABS[0]!;
  return hasVisualization ? 'visualization' : 'general';
}
